/**
 * Session storage.
 *
 * Staff and portal are separate builds on separate origins-or-paths and MUST NEVER
 * share a session. That is enforced here by the storage key: each app constructs its
 * own store with its own namespace, and there is no default namespace to fall into.
 *
 * `localStorage` is used deliberately over a cookie: the API is bearer-token based
 * (see `kernel/api/authentication.py`), the two apps must not send each other's
 * credentials, and a same-site cookie would be shared across paths on one host.
 */

export interface StoredSession {
  accessToken: string;
  /** Absolute epoch milliseconds. Null when the server did not state an expiry. */
  expiresAt: number | null;
  /** The membership this session is bound to. Null before an organization is chosen. */
  organizationId: string | null;
}

export interface SessionStore {
  read(): StoredSession | null;
  write(session: StoredSession): void;
  clear(): void;
  subscribe(listener: (session: StoredSession | null) => void): () => void;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const memoryStorage = (): StorageLike => {
  const map = new Map<string, string>();
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => void map.set(k, v),
    removeItem: (k) => void map.delete(k),
  };
};

function resolveStorage(): StorageLike {
  if (typeof window === 'undefined' || !window.localStorage) return memoryStorage();
  try {
    // Safari private mode throws on write rather than on access.
    const probe = '__legalcrm_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return memoryStorage();
  }
}

/**
 * @param namespace `staff` or `portal`. Required — there is no default, because a
 *   default is exactly how two audiences would end up on one key.
 */
export function createSessionStore(namespace: 'staff' | 'portal'): SessionStore {
  const key = `legalcrm.${namespace}.session`;
  const storage = resolveStorage();
  const listeners = new Set<(s: StoredSession | null) => void>();

  const emit = (session: StoredSession | null) => {
    for (const listener of listeners) listener(session);
  };

  // Another tab signing out must sign this one out too.
  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('storage', (event) => {
      if (event.key === key) emit(read());
    });
  }

  function read(): StoredSession | null {
    const raw = storage.getItem(key);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as StoredSession;
      if (typeof parsed?.accessToken !== 'string' || !parsed.accessToken) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  return {
    read,
    write(session) {
      storage.setItem(key, JSON.stringify(session));
      emit(session);
    },
    clear() {
      storage.removeItem(key);
      emit(null);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
}

/** True when the session has an expiry and it is within `skewMs` of now. */
export function isExpiring(session: StoredSession | null, skewMs = 60_000): boolean {
  if (!session?.expiresAt) return false;
  return session.expiresAt - Date.now() <= skewMs;
}
